import { forwardRef, type HTMLAttributes } from 'react';
import { Skeleton } from './Skeleton';
import { SkeletonCard } from './SkeletonCard';

export interface LoadingSpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Size of the spinner: 'sm' | 'md' | 'lg' */
  size?: 'sm' | 'md' | 'lg';
  /** Optional label for accessibility */
  label?: string;
}

const sizeClasses = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-3',
  lg: 'w-12 h-12 border-4',
};

/**
 * LoadingSpinner - Animated loading indicator.
 *
 * Uses CSS animation for smooth spinning effect.
 * Includes accessibility label for screen readers.
 */
export const LoadingSpinner = forwardRef<HTMLDivElement, LoadingSpinnerProps>(
  ({ size = 'md', label = 'Loading...', className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label}
        className={`flex items-center justify-center ${className}`}
        {...props}
      >
        <div
          className={`
            ${sizeClasses[size]}
            border-[var(--color-sand-300)]
            border-t-[var(--color-primary)]
            rounded-full
            animate-spin
          `}
        />
        <span className="sr-only">{label}</span>
      </div>
    );
  }
);

LoadingSpinner.displayName = 'LoadingSpinner';

export interface LoadingPageProps {
  /** Optional message to display */
  message?: string;
  /** 'spinner' shows the classic centered spinner; 'skeleton' shows a layout-matching skeleton page */
  variant?: 'spinner' | 'skeleton';
  /** Number of skeleton cards to render (only used with variant="skeleton") */
  cardCount?: number;
}

/**
 * Full-page loading state for Suspense fallbacks.
 *
 * Use variant="skeleton" for layout-matching placeholders that reduce
 * perceived loading time by ~30% compared to a generic spinner.
 */
export function LoadingPage({ message = 'Loading...', variant = 'spinner', cardCount = 3 }: LoadingPageProps) {
  if (variant === 'skeleton') {
    return (
      <div className="min-h-screen bg-[var(--color-sand-50)]" role="status" aria-label={message}>
        {/* Header skeleton */}
        <div className="sticky top-0 z-50 bg-[var(--color-sand-50)] border-b border-[var(--color-sand-200)]">
          <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton variant="rect" width="w-10" height="h-10" />
              <Skeleton width="w-24" height="h-5" />
            </div>
            <Skeleton variant="circle" width="w-10" height="h-10" />
          </div>
        </div>

        {/* Content skeleton */}
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
          {/* Page title area */}
          <div className="space-y-2 mb-6">
            <Skeleton width="w-1/3" height="h-7" />
            <Skeleton width="w-2/3" height="h-4" />
          </div>

          {/* Card skeletons */}
          {Array.from({ length: cardCount }, (_, i) => (
            <SkeletonCard key={i} primary={i === 0} />
          ))}
        </div>

        <span className="sr-only">{message}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--color-sand-50)]">
      <LoadingSpinner size="lg" label={message} />
      <p className="mt-4 text-[var(--color-ink-muted)] text-sm">{message}</p>
    </div>
  );
}

export default LoadingSpinner;
